<template>
  <el-dialog
    v-model="_isShow"
    width="90%"
    top="5vh"
    lock-scroll
    custom-class="rfid-item-selector-dialog"
    center
    show-close
    destroy-on-close
    :title="$t('Scan RFID')"
    @opened="onDialogOpened"
    @closed="onDialogClosed"
  >
    <div class="scan-status-bar">
      <el-button
        :type="isStatusScanning ? 'primary' : 'default'"
        size="small"
        @click="toggleScanning"
        class="scan-toggle-button"
      >
        <template v-if="isStatusScanning">
          <span class="scanning-indicator">●</span>
          {{ $t('Scanning') }}...
        </template>
        <template v-else>
          {{ $t('Start scanning') }}
        </template>
      </el-button>
      <div class="scan-count">
        {{ $t('total') }}: {{ rfidList.length }}
        <span v-if="selectedEpcs.size === 1" class="selected-count"> / {{ $t('selected') }}</span>
        <span v-else-if="selectedEpcs.size > 0" class="selected-count">
          / {{ $t('selected') }}: {{ selectedEpcs.size }}</span
        >
      </div>
    </div>
    <div class="rfid-scan-wrapper">
      <div class="rfid-scan-container">
        <div v-if="rfidList.length === 0" class="empty-state">
          <IconRfid class="empty-icon" />
          <p>{{ $t('Waiting for RFID scan') }}...</p>
        </div>

        <div v-else class="rfid-list">
          <div
            v-for="item in sortedRfidList"
            :key="item.epc"
            class="rfid-item"
            :class="{ 'rfid-item-selected': isSelected(item.epc), 'rfid-item-selectable': true }"
            @click="toggleSelection(item.epc)"
          >
            <div class="rfid-item-header">
              <div class="rfid-epc">
                <div class="selection-indicator">
                  <el-checkbox v-if="mode === 'multiple-select'" :model-value="isSelected(item.epc)" @click.stop />
                  <el-radio v-else :model-value="isSelected(item.epc)" :label="''" @click.stop />
                </div>
                <IconRfid class="rfid-icon" />
                <span>{{ item.epc }}</span>
              </div>
              <div class="rfid-rssi" :class="getRssiClass(item.rssi)">{{ formatRssi(item.rssi) }} dBm</div>
            </div>
            <div class="rfid-item-footer">
              <span class="scan-time">{{ formatTime(item.scannedAt) }}</span>
              <span v-if="mode === 'multiple-select'" class="scan-count-badge">{{ item.count }}x</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="select-all-container">
      <RfidPowerSwitch />
      <el-button v-if="mode === 'multiple-select'" @click="toggleSelectAll" :disabled="rfidList.length === 0">
        {{ isAllSelected ? $t('Deselect All') : $t('Select All') }}
      </el-button>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="clearList" :disabled="rfidList.length === 0">
          {{ $t('clear') }}
        </el-button>
        <el-button type="primary" @click="onConfirm" :disabled="selectedEpcs.size === 0">
          {{ $t('ok') }} {{ mode === 'multiple-select' ? `(${selectedEpcs.size})` : '' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { PropSync, Prop, Emit } from 'vue-property-decorator'
import IconRfid from '@/components/svg/IconRfid.vue'
import RfidPowerSwitch from '@/components/bizBrowser/RfidPowerSwitch.vue'
import { SendToBizBrowser } from '@/utils/bizBrowser/sendToBizBrowser'
import emitter from '@/utils/mitt'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import { format } from 'date-fns'

type RfidItem = RfidScanParsedResult & {
  scannedAt: Date
  count: number
}

@Options({
  components: {
    IconRfid,
    RfidPowerSwitch,
  },
  emits: ['update:isShow', 'confirm'],
  name: 'RfidItemSelector',
})
export default class RfidItemSelector extends Vue {
  @PropSync('isShow', { type: Boolean }) _isShow!: boolean
  @Prop({ type: String, default: 'single-select' }) readonly mode!: 'single-select' | 'multiple-select'

  rfidMap: Map<string, RfidItem> = new Map()
  currentTime: Date = new Date()
  updateTimeInterval?: number
  selectedEpcs: Set<string> = new Set()

  get isStatusScanning() {
    return this.$store.getters.bizBrowserState.isDeviceScanning
  }

  get rfidList(): RfidItem[] {
    return Array.from(this.rfidMap.values())
  }

  get sortedRfidList(): RfidItem[] {
    return [...this.rfidList].sort((a, b) => {
      const rssiA = parseFloat(a.rssi)
      const rssiB = parseFloat(b.rssi)
      return rssiB - rssiA
    })
  }

  get isAllSelected(): boolean {
    return this.rfidList.length > 0 && this.selectedEpcs.size === this.rfidList.length
  }

  onDialogOpened() {
    SendToBizBrowser.startScan()
    emitter.on('rfid:scanned', this.handleScannedResults)
    this.updateTimeInterval = window.setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
  }

  onDialogClosed() {
    SendToBizBrowser.idleMode()
    emitter.off('rfid:scanned', this.handleScannedResults)
    if (this.updateTimeInterval) {
      clearInterval(this.updateTimeInterval)
      this.updateTimeInterval = undefined
    }
    this.rfidMap.clear()
    this.selectedEpcs.clear()
  }

  toggleScanning() {
    if (this.isStatusScanning) {
      SendToBizBrowser.idleMode()
    } else {
      SendToBizBrowser.startScan()
    }
  }

  handleScannedResults(results?: RfidScanParsedResult[]) {
    if (!results) {
      return
    }
    results.forEach((result) => this.receiveScannedResult(result))
  }

  receiveScannedResult(result: RfidScanParsedResult) {
    const existingItem = this.rfidMap.get(result.epc)

    if (existingItem) {
      existingItem.rssi = result.rssi
      existingItem.scannedAt = new Date()
      existingItem.count++
    } else {
      this.rfidMap.set(result.epc, {
        ...result,
        scannedAt: new Date(),
        count: 1,
      })
    }

    this.rfidMap = new Map(this.rfidMap)
  }

  clearList() {
    this.rfidMap.clear()
    this.selectedEpcs.clear()
  }

  isSelected(epc: string): boolean {
    return this.selectedEpcs.has(epc)
  }

  toggleSelection(epc: string) {
    if (this.mode === 'single-select') {
      this.selectedEpcs.clear()
      this.selectedEpcs.add(epc)
    } else {
      if (this.selectedEpcs.has(epc)) {
        this.selectedEpcs.delete(epc)
      } else {
        this.selectedEpcs.add(epc)
      }
    }
    this.selectedEpcs = new Set(this.selectedEpcs)
  }

  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedEpcs.clear()
    } else {
      this.rfidList.forEach((item) => {
        this.selectedEpcs.add(item.epc)
      })
    }
    this.selectedEpcs = new Set(this.selectedEpcs)
  }

  get selectedItems(): RfidItem[] {
    return this.sortedRfidList.filter((item) => this.selectedEpcs.has(item.epc))
  }

  @Emit('confirm')
  emitConfirm() {
    return this.selectedItems
  }

  onConfirm() {
    this.emitConfirm()
    this._isShow = false
  }

  formatTime(date: Date): string {
    const diffInSeconds = Math.floor((this.currentTime.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return `${Math.max(diffInSeconds, 0)}${this.$t('seconds_ago')}`
    } else if (diffInSeconds < 5940) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}${this.$t('minutes_ago')}`
    } else {
      return format(date, 'HH:mm:ss')
    }
  }

  formatRssi(rssi: string): string {
    const rssiValue = parseFloat(rssi)
    return rssiValue.toFixed(1)
  }

  getRssiClass(rssi: string): string {
    const rssiValue = parseFloat(rssi)
    if (rssiValue >= -50) return 'rssi-excellent'
    if (rssiValue >= -60) return 'rssi-good'
    if (rssiValue >= -70) return 'rssi-fair'
    return 'rssi-weak'
  }
}
</script>

<style lang="scss" scoped>
.rfid-scan-wrapper {
  border-top: 1px solid #e4e7ed;
  border-bottom: 1px solid #e4e7ed;
}

.rfid-scan-container {
  max-height: 50vh;
  overflow-y: scroll;
  padding: 0.5rem 0;

  // Force scrollbar to always be visible
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.scan-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f5f7fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 500;

  .scan-toggle-button {
    font-weight: 500;

    .scanning-indicator {
      display: inline-block;
      margin-right: 0.25rem;
      color: #67c23a;
      animation: pulse 1.5s ease-in-out infinite;
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .scan-count {
    color: #606266;
    font-size: 0.9rem;

    .selected-count {
      color: #409eff;
      font-weight: 600;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #909399;

  .empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
  }
}

.rfid-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rfid-item {
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  user-select: none;

  &.rfid-item-selectable:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    border-color: #409eff;
  }

  &.rfid-item-selected {
    background-color: #ecf5ff;
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
  }
}

.rfid-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.rfid-epc {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: monospace;
  font-size: 0.9rem;
  font-weight: 500;
  color: #303133;
  word-break: break-all;
  flex: 1;

  .selection-indicator {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    :deep(.el-radio) {
      display: none;
    }
  }

  .rfid-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .rfid-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
  }

  .rfid-url {
    color: #409eff;
    text-decoration: none;
    word-break: break-all;
    transition: color 0.2s ease;

    &:hover {
      color: #66b1ff;
      text-decoration: underline;
    }
  }

  .data-type-badge {
    display: inline-block;
    font-size: 0.7rem;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    width: fit-content;

    &.badge-epc {
      background-color: #e6f7ff;
      color: #0284c7;
    }

    &.badge-url {
      background-color: #f0f9ff;
      color: #7c3aed;
    }

    &.badge-unknown {
      background-color: #fef2f2;
      color: #dc2626;
    }
  }
}

.rfid-rssi {
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;

  &.rssi-excellent {
    background-color: #f0f9ff;
    color: #0284c7;
  }

  &.rssi-good {
    background-color: #f0fdf4;
    color: #16a34a;
  }

  &.rssi-fair {
    background-color: #fefce8;
    color: #ca8a04;
  }

  &.rssi-weak {
    background-color: #fef2f2;
    color: #dc2626;
  }
}

.rfid-item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #909399;

  .scan-time {
    font-family: monospace;
  }

  .scan-count-badge {
    background-color: #f4f4f5;
    color: #606266;
    padding: 0.15rem 0.5rem;
    border-radius: 12px;
    font-weight: 500;
  }
}

.select-all-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
}

.dialog-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
</style>

<style lang="scss">
.rfid-item-selector-dialog.el-dialog--center .el-dialog__body {
  padding: 1rem 1.25rem 0.5rem 1.25rem !important;
}
</style>
